import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

interface BlockRow {
  id: string;
  code: string;
  name: string;
  block_mode: string;
}
interface Interval {
  fromDate?: string;
  toDate?: string;
  recurringFromMonth?: number;
  recurringFromDay?: number;
  recurringToMonth?: number;
  recurringToDay?: number;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function BlockAnnualLeave() {
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [header, setHeader] = useState({ code: '', name: '', blockMode: 'FIXED' });
  const [intervals, setIntervals] = useState<Interval[]>([]);
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);
  const [employeeNames, setEmployeeNames] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<BlockRow | null>(null);
  const [error, setError] = useState('');

  const load = async () => {
    const res = await api.get('/lms/masters/block-al');
    setRows(res.data.blockAl || []);
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setHeader({ code: '', name: '', blockMode: 'FIXED' });
    setIntervals([{}]);
    setEmployeeIds([]);
    setEmployeeNames({});
    setError('');
    setShowForm(true);
  };

  const openEdit = async (row: BlockRow) => {
    setEditingId(row.id);
    setHeader({ code: row.code, name: row.name, blockMode: row.block_mode });
    const res = await api.get(`/lms/masters/block-al/${row.id}`);
    setIntervals((res.data.intervals || []).map((i: any) => ({
      fromDate: i.from_date, toDate: i.to_date,
      recurringFromMonth: i.recurring_from_month, recurringFromDay: i.recurring_from_day,
      recurringToMonth: i.recurring_to_month, recurringToDay: i.recurring_to_day,
    })));
    const emps = res.data.employees || [];
    setEmployeeIds(emps.map((e: any) => e.id));
    const names: Record<string, string> = {};
    for (const e of emps) names[e.id] = `${e.first_name} ${e.last_name} (${e.code})`;
    setEmployeeNames(names);
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      let id = editingId;
      if (id) {
        await api.put(`/lms/masters/block-al/${id}`, header);
      } else {
        const res = await api.post('/lms/masters/block-al', header);
        id = res.data.blockAl.id;
      }
      await api.put(`/lms/masters/block-al/${id}/intervals`, { intervals });
      await api.put(`/lms/masters/block-al/${id}/employees`, { employeeIds });
      setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/lms/masters/block-al/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Block Annual Leave</h1>
        <Button onClick={openCreate}>+ New</Button>
      </div>
      <Table
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          { key: 'block_mode', header: 'Mode' },
          {
            key: 'actions', header: 'Actions',
            render: (row) => (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => openEdit(row)}>Edit</Button>
                <Button variant="ghost" className="text-red-600" onClick={() => setDeleteTarget(row)}>Delete</Button>
              </div>
            ),
          },
        ]}
        rows={rows}
      />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Block Annual Leave' : 'New Block Annual Leave'}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} value={header.code} onChange={(e) => setHeader({ ...header, code: e.target.value })} required />
          <Input label="Name" maxLength={50} value={header.name} onChange={(e) => setHeader({ ...header, name: e.target.value })} required />
          <Select
            label="Mode"
            value={header.blockMode}
            onChange={(e) => setHeader({ ...header, blockMode: e.target.value })}
            options={[{ value: 'FIXED', label: 'Fixed (specific dates)' }, { value: 'RECURRING', label: 'Recurring (yearly)' }]}
          />

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">Blocked Intervals</label>
              <Button type="button" variant="ghost" onClick={() => setIntervals([...intervals, {}])}>+ Add</Button>
            </div>
            {intervals.map((iv, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-2 border rounded p-2 mb-2">
                {header.blockMode === 'FIXED' ? (
                  <>
                    <Input label="From" type="date" value={iv.fromDate || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, fromDate: e.target.value }; setIntervals(next);
                    }} />
                    <Input label="To" type="date" value={iv.toDate || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, toDate: e.target.value }; setIntervals(next);
                    }} />
                  </>
                ) : (
                  <>
                    <Input label="From Month/Day" placeholder="MM" type="number" value={iv.recurringFromMonth || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, recurringFromMonth: Number(e.target.value) }; setIntervals(next);
                    }} />
                    <Input label="" placeholder="DD" type="number" value={iv.recurringFromDay || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, recurringFromDay: Number(e.target.value) }; setIntervals(next);
                    }} />
                    <Input label="To Month/Day" placeholder="MM" type="number" value={iv.recurringToMonth || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, recurringToMonth: Number(e.target.value) }; setIntervals(next);
                    }} />
                    <Input label="" placeholder="DD" type="number" value={iv.recurringToDay || ''} onChange={(e) => {
                      const next = [...intervals]; next[idx] = { ...iv, recurringToDay: Number(e.target.value) }; setIntervals(next);
                    }} />
                  </>
                )}
                <Button type="button" variant="ghost" className="text-red-600 col-span-2" onClick={() => setIntervals(intervals.filter((_, i) => i !== idx))}>
                  Remove
                </Button>
              </div>
            ))}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Assigned Employees</label>
            <LookupSelect
              value=""
              placeholder="Add employee..."
              fetchOptions={fetchEmployeeOptions}
              onChange={(v, opt) => {
                if (!v || employeeIds.includes(v)) return;
                setEmployeeIds([...employeeIds, v]);
                if (opt) setEmployeeNames({ ...employeeNames, [v]: opt.label });
              }}
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {employeeIds.map((id) => (
                <span key={id} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                  {employeeNames[id] || id}
                  <button type="button" onClick={() => setEmployeeIds(employeeIds.filter((e) => e !== id))} title="Remove">&times;</button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit">Save</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete"
        message={`Delete "${deleteTarget?.name}"? This cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
